/**
 * svelte-result-store v1.1.0
 * https://github.com/greymass/svelte-result-store
 *
 * @license
 * Copyright (c) 2021 FFF00 Agents AB & Greymass Inc. All Rights Reserved.
 * 
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 * 
 *  1. Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 * 
 *  2. Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 * 
 *  3. Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 * 
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR INTENDED FOR USE
 * IN THE DESIGN, CONSTRUCTION, OPERATION OR MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { writable as writable$1, derived as derived$1, readable as readable$1 } from 'svelte/store';

class ReadableResult {
    subscribe;
    constructor(resultStore) {
        this.subscribe = resultStore.subscribe;
    }
    /**
     * A store containing the value or undefined if there is an error.
     * Can also be undefined for async stores while the value is being resolved.
     */
    get value() {
        return {
            subscribe: (set) => this.subscribe((result) => {
                if (result.error === undefined) {
                    set(result.value);
                }
            }),
        };
    }
    /**
     * A store always containing a value when the result is resolved.
     * @param value The value used in place of errors.
     */
    catch(handler) {
        return {
            subscribe: (set) => this.subscribe((result) => {
                if (result.error !== undefined) {
                    const value = handler(result.error);
                    if (value) {
                        set(value);
                    }
                }
                else if (result.value !== undefined) {
                    set(result.value);
                }
            }),
        };
    }
    /** Transform the results to a new ReadableResult store. */
    map(transform) {
        const store = {
            subscribe: (set) => this.subscribe((result) => {
                if (result.error !== undefined || result.value !== undefined) {
                    let newResult = {};
                    try {
                        newResult = transform(result);
                    }
                    catch (error) {
                        newResult.error = error;
                    }
                    set(newResult);
                }
            }),
        };
        return new ReadableResult(store);
    }
    /** Transform the result value to a new ReadableResult store. */
    mapValue(transform) {
        const store = {
            subscribe: (set) => this.subscribe((result) => {
                if (result.value !== undefined) {
                    let value;
                    try {
                        value = transform(result.value);
                    }
                    catch (error) {
                        set({ error });
                        return;
                    }
                    set({ value });
                }
            }),
        };
        return new ReadableResult(store);
    }
    /** Transform the results to a new ReadableResult store by flattening the returned readable. */
    flatMap(transform) {
        const store = {
            subscribe: (set) => this.subscribe((result) => {
                if (result.error !== undefined || result.value !== undefined) {
                    let value;
                    try {
                        value = transform(result);
                    }
                    catch (error) {
                        set({ error });
                        return;
                    }
                    set({ value });
                }
            }),
        };
        return flatten(store);
    }
    /** Transform the result value to a new ReadableResult store by flattening the returned readable. */
    flatMapValue(transform) {
        const store = {
            subscribe: (set) => this.subscribe((result) => {
                if (result.value !== undefined) {
                    let value;
                    try {
                        value = transform(result.value);
                    }
                    catch (error) {
                        set({ error });
                        return;
                    }
                    set({ value });
                }
            }),
        };
        return flatten(store);
    }
    /**
     * A store containing the error or undefined.
     */
    get error() {
        return {
            subscribe: (set) => this.subscribe((result) => {
                if (result.error !== undefined) {
                    set(result.error);
                }
            }),
        };
    }
    /**
     * A store containing true if the readable has a result (a value or error), false otherwise.
     */
    get resolved() {
        return {
            subscribe: (set) => this.subscribe((result) => {
                set(result.error !== undefined || result.value !== undefined);
            }),
        };
    }
    /**
     * A promise that resolves or rejects on the first value or error.
     */
    get promise() {
        return new Promise((resolve, reject) => {
            const done = this.subscribe((result) => {
                if (result.error !== undefined) {
                    reject(result.error);
                }
                else if (result.value !== undefined) {
                    resolve(result.value);
                }
                if (result.error !== undefined || result.value !== undefined) {
                    setTimeout(() => {
                        done();
                    }, 0);
                }
            });
        });
    }
}
class WritableResult extends ReadableResult {
    set;
    update;
    constructor(resultStore) {
        super(resultStore);
        this.set = resultStore.set;
        this.update = resultStore.update;
    }
    updateValue(updater) {
        this.update((result) => ({ value: updater(result.value) }));
    }
}
function readable(...args) {
    return new ReadableResult(internalWritable(...args));
}
function writable(...args) {
    return new WritableResult(internalWritable(...args));
}
function derived(stores, fn) {
    const single = !Array.isArray(stores);
    const auto = fn.length < 2;
    const store = derived$1(stores, (storeValues, set) => {
        const results = single
            ? [storeValues]
            : storeValues;
        const error = results.find((r) => r.error !== undefined);
        if (error) {
            set(error);
        }
        else {
            const values = results.map((r) => r.value);
            if (values.every((v) => v !== undefined)) {
                try {
                    const rv = fn(single ? values[0] : values, (value) => {
                        set({ value });
                    }, (error) => {
                        set({ error });
                    });
                    if (rv instanceof Promise) {
                        rv.then((value) => {
                            set({ value });
                        }).catch((error) => {
                            set({ error });
                        });
                    }
                    else if (auto) {
                        set({ value: rv });
                    }
                    else {
                        return rv;
                    }
                }
                catch (error) {
                    set({ error });
                }
            }
            else {
                set({});
            }
        }
    });
    return new ReadableResult(store);
}
/**
 * Takes nested readable and flattens it down to just one.
 * @param maxDepth Maximum recursion depth, default 10.
 */
function flatten(store, maxDepth) {
    const max = maxDepth || 10;
    const result = {};
    const flat = readable$1(result, (set) => {
        const next = (d) => (r) => {
            if (r.error) {
                set({ error: r.error });
            }
            else {
                if (r.value && typeof r.value.subscribe === 'function' && d < max) {
                    return subscribeCleanup(r.value, next(d + 1));
                }
                else {
                    set(r);
                }
            }
        };
        return subscribeCleanup(store, next(0));
    });
    return new ReadableResult(flat);
}
function subscribeCleanup(store, run) {
    let cleanup = noop;
    const unsub = store.subscribe((v) => {
        cleanup();
        cleanup = run(v) || noop;
    });
    return () => {
        cleanup();
        unsub();
    };
}
function internalWritable(...args) {
    let start;
    let result = {};
    if (args.length === 2) {
        result = args[0];
        start = args[1];
    }
    else {
        start = typeof args[0] === 'function' ? args[0] : noop;
        result =
            typeof args[0] === 'object' &&
                (args[0].value !== undefined || args[0].error !== undefined)
                ? args[0]
                : {};
    }
    return writable$1(result, (setResult) => {
        try {
            const rv = start((value) => {
                setResult({ value });
            }, (error) => {
                setResult({ error });
            });
            if (rv instanceof Promise) {
                rv.then((value) => {
                    if (value !== undefined) {
                        setResult({ value });
                    }
                }).catch((error) => {
                    setResult({ error });
                });
            }
            else {
                return rv;
            }
        }
        catch (error) {
            setResult({ error });
        }
    });
}
// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() { }

export { ReadableResult, WritableResult, derived, flatten, readable, writable };
//# sourceMappingURL=svelte-result-store.m.js.map
